import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid2 as Grid,
  Paper,
  Alert,
  useMediaQuery,
} from "@mui/material";
import { Mail, Phone, Clock, Send } from "lucide-react";
import MosaicPlaceholder from "../components/MosaicPlaceholder";
import theme from "../styles/theme";
import {
  ContactFormData,
  sendContactMessage,
} from "../services/contactService";

const ContactPage: React.FC = () => {
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  const [formData, setFormData] = useState<ContactFormData>({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"success" | "error" | null>(
    null
  );
  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    window.scrollTo(0, 0)
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    // Clear any previous status when the user starts typing again
    if (submitStatus) {
      setSubmitStatus(null);
      setErrorMessage("");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage("");

    try {
      await sendContactMessage(formData);
      setSubmitStatus("success");
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      setSubmitStatus("error");
      setErrorMessage(
        error instanceof Error ? error.message : "An unexpected error occurred"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container maxWidth="lg">
      <MosaicPlaceholder />
      <Grid container spacing={4}>
        {isMobile ?
          <Grid size={{ xs: 12, md: 7 }}>
            <Paper className="p-6" style={{ paddingTop: "0px" }}>
              <Typography style={{ color: "#00537E"}} variant="h6" className="mb-4">
                Send us a Message
              </Typography>
              <form onSubmit={handleSubmit} className="space-y-4">
                <TextField
                  size="small"
                  fullWidth
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
                <TextField
                size="small"
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
                <TextField
                size="small"
                  fullWidth
                  label="Phone Number"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                  inputProps={{
                    pattern: "^[0-9()-. ]+$",
                    title: "Please enter a valid phone number",
                  }}
                />
                <TextField
                  fullWidth
                  label="Message"
                  name="message"
                  multiline
                  rows={4}
                  value={formData.message}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
                {/* 
            <Box className="pt-2">
              <ReCAPTCHA
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ""}
                onChange={(value) => setRecaptchaValue(value)}
              />
            </Box> */}

                {submitStatus === "success" && (
                  <Alert severity="success">
                    Your message has been sent successfully. We'll respond within
                    3 business days.
                  </Alert>
                )}

                {submitStatus === "error" && (
                  <Alert severity="error">{errorMessage}</Alert>
                )}

                <Button
                  type="submit"
                  variant="contained"
                  style={{ backgroundColor: "#00537E", fontSize: "16px" }}
                  fullWidth
                  size="large"
                  disabled={isSubmitting}
                  startIcon={<Send size={20} />}
                >
                  {isSubmitting ? "Sending..." : "Send Message"}
                </Button>
              </form>
            </Paper>
          </Grid>
          :
          <Grid size={{ xs: 12, md: 5 }}>
            <Paper elevation={0} style={{ backgroundColor: "#e7e7e8" }} className="p-6">
              <Box className="space-y-6">
                <Box>
                  <Typography variant="h6" className="mb-2">
                  Operation RAILSAFE Partner Training Program
                  </Typography>
                  <Typography
                    variant="body1"
                    className="flex items-center gap-2 mb-1"
                  >
                    <Mail size={20} className="text-blue-600" />
                    <a
                      href="mailto:railsafe@amtrak.com"
                      className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                      railsafe@amtrak.com
                    </a>
                  </Typography>
                  <Typography variant="body2" className="text-gray-600 ml-7">
                    To request more information or training.
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="h6" className="mb-2">
                    Emergency Management Team
                  </Typography>
                  <Typography
                    variant="body1"
                    className="flex items-center gap-2 mb-1"
                  >
                    <Mail size={20} className="text-blue-600" />
                    <a
                      href="mailto:emergencymanagement@amtrak.com"
                      className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                      emergencymanagement@amtrak.com
                    </a>
                  </Typography>
                  <Typography variant="body2" className="text-gray-600 ml-7">
                    To request Amtrak Passenger Train Emergency Response (PTER)
                    training.
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="h6" className="mb-2">
                    Emergency Contact
                  </Typography>
                  <Typography variant="body1" className="flex items-center gap-2">
                    <Phone size={20} className="text-red-600" />
                    <span className="font-medium">1-800-331-0008</span>
                  </Typography>
                  <Typography variant="body2" className="text-gray-600 ml-7">
                    To contact Amtrak’s National Communications Center.
                  </Typography>
                </Box>

                <Alert
                  icon={<Clock className="text-gray-600" size={20} />}
                  severity="info"
                  className="bg-gray-100"
                >
                  Note: Email responses are typically sent within three business days. This mailbox is not monitored 24/7.
                </Alert>
              </Box>
            </Paper>
          </Grid>
        }

        {isMobile ?
          <Grid size={{ xs: 12, md: 5 }}>
            <Paper elevation={0} style={{ backgroundColor: "#e7e7e8" }} className="p-6">
              <Box className="space-y-6">
                <Box>
                  <Typography variant="h6" className="mb-2">
                    RAILSAFE Partner Training Program
                  </Typography>
                  <Typography
                    variant="body1"
                    className="flex items-center gap-2 mb-1"
                  >
                    <Mail size={20} className="text-blue-600" />
                    <a
                      href="mailto:railsafe@amtrak.com"
                      className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                      railsafe@amtrak.com
                    </a>
                  </Typography>
                  <Typography variant="body2" className="text-gray-600 ml-7">
                    To request more information or training.
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="h6" className="mb-2">
                    Emergency Management Team
                  </Typography>
                  <Typography
                    variant="body1"
                    className="flex items-center gap-2 mb-1"
                  >
                    <Mail size={20} className="text-blue-600" />
                    <a
                      href="mailto:emergencymanagement@amtrak.com"
                      className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                      emergencymanagement&#8203;@amtrak.com
                    </a>
                  </Typography>
                  <Typography variant="body2" className="text-gray-600 ml-7">
                    To request Amtrak Passenger Train Emergency Response (PTER)
                    training.
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="h6" className="mb-2">
                    Emergency Contact
                  </Typography>
                  <Typography variant="body1" className="flex items-center gap-2">
                    <Phone size={20} className="text-red-600" />
                    <span className="font-medium">1-800-331-0008</span>
                  </Typography>
                  <Typography variant="body2" className="text-gray-600 ml-7">
                    To contact Amtrak’s National Communications Center.
                  </Typography>
                </Box>

                <Alert
                  icon={<Clock className="text-gray-600" size={20} />}
                  severity="info"
                  className="bg-gray-100"
                >
                  Note: Email responses are typically sent within three business days. This mailbox is not monitored 24/7.
                </Alert>
              </Box>
            </Paper>
          </Grid>
          :
          <Grid size={{ xs: 12, md: 7 }}>
            <Paper className="p-6">
              <Typography style={{ color: "#00537E"}} variant="h6" className="mb-4">
                Send us a Message
              </Typography>
              <form onSubmit={handleSubmit} className="space-y-4">
                <TextField
                  fullWidth
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                  inputProps={{
                    pattern: "^[0-9()-. ]+$",
                    title: "Please enter a valid phone number",
                  }}
                />
                <TextField
                  fullWidth
                  label="Message"
                  name="message"
                  multiline
                  rows={4}
                  value={formData.message}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
                {/* 
              <Box className="pt-2">
                <ReCAPTCHA
                  sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ""}
                  onChange={(value) => setRecaptchaValue(value)}
                />
              </Box> */}

                {submitStatus === "success" && (
                  <Alert severity="success">
                    Your message has been sent successfully. We'll respond within
                    3 business days.
                  </Alert>
                )}

                {submitStatus === "error" && (
                  <Alert severity="error">{errorMessage}</Alert>
                )}

                <Button
                  type="submit"
                  variant="contained"
                  style={{ backgroundColor: "#00537E", fontSize: "16px" }}
                  fullWidth
                  size="large"
                  disabled={isSubmitting}
                  startIcon={<Send size={20} />}
                >
                  {isSubmitting ? "Sending..." : "Send Message"}
                </Button>
              </form>
            </Paper>
          </Grid>
        }
      </Grid>
    </Container>
  );
};

export default ContactPage;
